/**
 * EditorToolbar — V14.62 Sprint 19 (Lab).
 *
 * Outils :
 *   • Sélectionner (V) — drag les murs & poignées
 *   • Dessiner un Mur (D) — 2 clics canvas (Shift = ortho-snap 0°/90°/180°/270°)
 *   • Placer une Fenêtre (W) — clic sur un mur
 *   • Supprimer (E) — clic sur un mur
 *   • Calibration / Règle (C) — 2 clics + modale métrique
 *
 * + Annuler / Rétablir (Ctrl+Z / Ctrl+Y, via zundo)
 * + Badge d'échelle live affichant scale_m_per_px (ou « non calibré »)
 */
import {
  MousePointer2, Plus, Square, Trash2, RotateCcw, Ruler,
  Undo2, Redo2, DoorOpen, StretchHorizontal, LandPlot, TriangleRight, Frame,
} from 'lucide-react';
import { useStore } from 'zustand';
import { useGeometryStore, TOOLS, WALL_KIND_DEFS } from './useGeometryStore';
import BackgroundDropzone from './BackgroundDropzone';

const TOOL_BUTTONS = [
  { tool: TOOLS.SELECT,        icon: MousePointer2,     label: 'Sélectionner',    hint: 'V — glisser-déposer les murs' },
  { tool: TOOLS.DRAW_WALL,     icon: Plus,              label: 'Dessiner un Mur', hint: 'D — Shift pour ortho-snap' },
  { tool: TOOLS.PLACE_WINDOW,  icon: Square,            label: 'Fenêtre',         hint: 'W — clic sur un mur' },
  { tool: TOOLS.PLACE_DOOR,    icon: DoorOpen,          label: 'Porte',           hint: 'P — clic sur un mur' },
  { tool: TOOLS.PLACE_STAIRS,  icon: StretchHorizontal, label: 'Escalier',        hint: 'S — tracez la zone, puis choisissez le type' },
  { tool: TOOLS.DRAW_TERRAIN,  icon: LandPlot,          label: 'Terrain',         hint: 'T — cliquez chaque sommet, double-clic ou Entrée pour fermer (RDC)' },
  { tool: TOOLS.DRAW_SKEW,     icon: TriangleRight,     label: 'Ligne de brisure', hint: 'B — 2 clics : trace la ligne où le plafond commence à descendre (mansarde)' },
  { tool: TOOLS.PLACE_VELUX,   icon: Frame,             label: 'Velux',            hint: 'X — clic sur une ligne de brisure pour poser un velux' },
  { tool: TOOLS.DELETE,        icon: Trash2,            label: 'Supprimer',       hint: 'E — clic sur un mur ou escalier' },
  { tool: TOOLS.CALIBRATE,     icon: Ruler,             label: 'Règle',           hint: 'C — calibration métrique' },
];

const ScaleBadge = ({ scale }) => {
  const calibrated = scale > 0;
  return (
    <div
      data-testid="scale-badge"
      title={calibrated ? `1 m = ${(1 / scale).toFixed(1)} px` : 'Plan non calibré — utilisez l\'outil Règle (C)'}
      className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded text-[10.5px] border font-mono ${
        calibrated
          ? 'bg-emerald-500/15 border-emerald-400/50 text-emerald-200'
          : 'bg-amber-500/10 border-amber-400/40 text-amber-300'
      }`}
    >
      <Ruler className="w-3 h-3" />
      {calibrated
        ? <>1 m = <span className="font-semibold">{(1 / scale).toFixed(1)}</span> px</>
        : <>non calibré</>}
    </div>
  );
};

const EditorToolbar = () => {
  const tool = useGeometryStore((s) => s.tool);
  const setTool = useGeometryStore((s) => s.setTool);
  const resetSeed = useGeometryStore((s) => s.resetSeed);
  const wallsCount = useGeometryStore((s) => s.walls.length);
  const scale = useGeometryStore((s) => s.scale_m_per_px);
  const drawWallKind = useGeometryStore((s) => s.drawWallKind);
  const setDrawWallKind = useGeometryStore((s) => s.setDrawWallKind);

  // Temporal (undo/redo) — zundo 2.x exposes `useGeometryStore.temporal`
  // as a *vanilla* store, so we subscribe via zustand's `useStore` helper.
  const pastStates = useStore(useGeometryStore.temporal, (t) => t.pastStates);
  const futureStates = useStore(useGeometryStore.temporal, (t) => t.futureStates);
  const canUndo = pastStates.length > 0;
  const canRedo = futureStates.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-lg border"
         style={{ background: 'rgba(8,12,20,0.7)', borderColor: 'rgba(16,185,129,0.25)' }}
         data-testid="editor-toolbar">
      <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/70 mr-2 select-none">
        Outils
      </div>

      {TOOL_BUTTONS.map(({ tool: t, icon: Icon, label, hint }) => {
        const active = tool === t;
        return (
          <button
            key={t}
            type="button"
            onClick={() => setTool(t)}
            title={`${label} — ${hint}`}
            data-testid={`tool-${t}`}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded text-[11px] border transition-colors ${
              active
                ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-100'
                : 'bg-black/30 border-white/10 text-gray-300 hover:bg-white/5 hover:border-emerald-400/30'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="hidden md:inline">{label}</span>
          </button>
        );
      })}

      {/* Separator */}
      <div className="h-6 w-px bg-white/10 mx-1" />

      {/* Wall-kind chip picker — visible only while the wall tool is active.
          Mirrors the production Édition Plan classification scheme. */}
      {tool === TOOLS.DRAW_WALL && (
        <div className="flex items-center gap-1" data-testid="wall-kind-picker">
          <span className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mr-1">
            Type
          </span>
          {WALL_KIND_DEFS.map((k) => {
            const active = drawWallKind === k.value;
            return (
              <button key={k.value}
                      type="button"
                      onClick={() => setDrawWallKind(k.value)}
                      title={k.description}
                      data-testid={`wall-kind-${k.value}`}
                      aria-pressed={active}
                      className={`inline-flex items-center gap-1 h-7 px-2 rounded text-[10.5px] border transition-colors ${
                        active
                          ? 'border-emerald-400/70 bg-emerald-500/15 text-emerald-100'
                          : 'border-white/10 bg-black/30 text-gray-300 hover:border-emerald-400/30'
                      }`}>
                <span className="inline-block w-2 h-2 rounded-full"
                      style={{ background: k.color,
                               boxShadow: active ? `0 0 0 2px ${k.color}33` : 'none' }} />
                {k.label}
              </button>
            );
          })}
          <div className="h-6 w-px bg-white/10 mx-1" />
        </div>
      )}

      <button
        type="button"
        onClick={() => useGeometryStore.temporal.getState().undo()}
        disabled={!canUndo}
        title="Annuler (Ctrl+Z)"
        data-testid="tool-undo"
        className="inline-flex items-center justify-center w-8 h-8 rounded border bg-black/30 border-white/10 text-gray-300 hover:bg-white/5 hover:border-emerald-400/30 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Undo2 className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => useGeometryStore.temporal.getState().redo()}
        disabled={!canRedo}
        title="Rétablir (Ctrl+Shift+Z ou Ctrl+Y)"
        data-testid="tool-redo"
        className="inline-flex items-center justify-center w-8 h-8 rounded border bg-black/30 border-white/10 text-gray-300 hover:bg-white/5 hover:border-emerald-400/30 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Redo2 className="w-3.5 h-3.5" />
      </button>

      <div className="flex-1" />

      <BackgroundDropzone />

      <ScaleBadge scale={scale} />

      <span className="text-[10px] text-gray-500 mx-2 font-mono">
        {wallsCount} mur{wallsCount > 1 ? 's' : ''}
      </span>

      <button
        type="button"
        onClick={resetSeed}
        title="Recharger l'état initial"
        data-testid="tool-reset"
        className="inline-flex items-center gap-1 h-8 px-2.5 rounded text-[11px] border bg-black/30 border-white/10 text-gray-400 hover:text-gray-200 hover:border-white/30"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        <span className="hidden md:inline">Réinitialiser</span>
      </button>
    </div>
  );
};

export default EditorToolbar;
